/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */
 

import { parallaxImages } from "../common/animations/parrallaxImages.js";

/**
 * Unified GSAP Animation Handler for Full Width Banner
 * Handles both pinning and white overlay animations efficiently
 */
const createFullWidthBannerAnimations = (selector) => {
	// Early returns for missing dependencies
	if (typeof gsap === 'undefined' || !window.ScrollTrigger) {
		return { pinTriggers: [], overlayTriggers: [] };
	}

	gsap.registerPlugin(ScrollTrigger);
	
	const elements = document.querySelectorAll(selector);
	if (!elements.length) {
		return { pinTriggers: [], overlayTriggers: [] };
	}

	const pinTriggers = [];
	const overlayTriggers = [];

	elements.forEach((element, index) => {
		// Create pin animation
		const pinTrigger = ScrollTrigger.create({
			trigger: element,
			start: "top top",
			end: "bottom top",
			pin: true,
			pinSpacing: false,
			anticipatePin: 1,
			refreshPriority: -1,
			onToggle: (self) => {
				element.classList.toggle("is-pinned", self.isActive);
			}
		});
		pinTriggers.push(pinTrigger);

		// Create white overlay animation
		const whiteOverlay = element.querySelector('.full-width-banner__white-overlay');
		if (whiteOverlay) {
			const overlayTrigger = gsap.fromTo(whiteOverlay, 
				{ y: "100%" },
				{ 
					y: "0%",
					ease: "none",
					scrollTrigger: {
						trigger: element,
						start: "top top",
						end: "bottom top",
						scrub: true
					}
				}
			);
			overlayTriggers.push(overlayTrigger.scrollTrigger);
		}
	});

	return { pinTriggers, overlayTriggers };
};

/**
 * Cleanup ScrollTriggers efficiently
 */
const cleanupScrollTriggers = (...triggerArrays) => {
	triggerArrays.forEach(triggers => {
		if (Array.isArray(triggers)) {
			triggers.forEach(trigger => trigger?.kill?.());
		}
	});
};

function ensureYoutubeAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.YT && window.YT.Player) {
			return resolve();
		}

		let otherYoutubeReadyCb = null;
		if (typeof window.onYouTubeIframeAPIReady !== "undefined") {
			otherYoutubeReadyCb = window.onYouTubeIframeAPIReady;
		}

		window.onYouTubeIframeAPIReady = () => {
			otherYoutubeReadyCb && otherYoutubeReadyCb();
			resolve();
		};

		let script = document.createElement("script");
		script.id = "youtube-iframe-script";
		script.src = "https://www.youtube.com/iframe_api";
		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

function ensureVimeoAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.Vimeo && window.Vimeo.Player) {
			return resolve();
		}

		let script = document.createElement("script");
		script.id = "vimeo-iframe-script";
		script.src = "https://player.vimeo.com/api/player.js";

		// Set up onload event handler
		script.onload = () => {
			// Check if the necessary objects are now available
			if (window.Vimeo && window.Vimeo.Player) {
				resolve();
			} else {
				reject(
					new Error(
						"Vimeo API script loaded, but Vimeo or Vimeo.Player is still not available",
					),
				);
			}
		};

		// Set up onerror event handler
		script.onerror = () => {
			reject(new Error("Error loading Vimeo API script"));
		};

		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

const removePosterImage = (video) => {
	const poster = video.querySelector(".full-width-banner__background-image");

	if (poster) {
		poster.style.opacity = 0;
	}
};

const handleIframeVideos = () => {
	const videoCollection = document.getElementsByClassName(
		"full-width-banner__background-video-wrapper",
	);

	if (videoCollection) {
		const videoArray = Array.from(videoCollection);

		videoArray.forEach((videoItem) => {
			// handle local videos
			if (videoItem.dataset.videoType === "video-local") {
				const frame = videoItem.getElementsByTagName("video");
				frame[0].addEventListener("play", () => {
					removePosterImage(videoItem);
				});
			}

			// handle youtube videos
			if (videoItem.dataset.videoType === "video-youtube") {
				ensureYoutubeAPIReady().then(() => {
					videoArray.forEach((video, i) => {
						// events if youtube video is loaded
						const frame = video.getElementsByTagName("iframe");
						if (frame.length) {
							const player = new YT.Player(frame[0], {
								events: {
									onReady: () => {
										isPlayerReady = true;
									},
									onStateChange: (e) => {
										if (e.target.getPlayerState() === 1) {
											removePosterImage(video);
										}

										if (e.target.getPlayerState() === 0) {
											e.target.playVideo();
										}
									},
								},
							});
						}
					});
				});
			}

			// handle vimeo videos
			if (videoItem.dataset.videoType === "video-vimeo") {
				ensureVimeoAPIReady().then(() => {
					videoArray.forEach((video, i) => {
						// events if Vimeo video is loaded
						const frame = video.getElementsByTagName("iframe");

						if (frame.length) {
							if (video.dataset.videoType === "video-vimeo") {
								const player = new Vimeo.Player(frame[0]);

								player.on("play", () => {
									removePosterImage(video);
								});
							}
						}
					});
				});
			}
		});
	}
};

// Store ScrollTrigger instances
let animations = { pinTriggers: [], overlayTriggers: [] };

const initFullWidthBannerAnimations = () => {
	// Skip if GSAP not available
	if (typeof gsap === 'undefined') return;

	// Cleanup existing animations
	cleanupScrollTriggers(animations.pinTriggers, animations.overlayTriggers);

	// Get elements and create animations
	const wrappers = document.querySelectorAll(".wp-block-create-block-full-width-banner");
	
	if (wrappers.length > 0) {
		// Apply parallax effect
		parallaxImages(wrappers);
		
		// Create all GSAP animations at once
		animations = createFullWidthBannerAnimations(".wp-block-create-block-full-width-banner");
	}
};

// Initialize everything
const initFullWidthBanner = () => {
	handleIframeVideos();
	
	// Small delay to ensure GSAP is loaded
	setTimeout(initFullWidthBannerAnimations, 100);
};

if (document.readyState !== "loading") {
	initFullWidthBanner();
} else {
	document.addEventListener("DOMContentLoaded", initFullWidthBanner);
}

// Event listeners
window.addEventListener("beforeunload", () => {
	cleanupScrollTriggers(animations.pinTriggers, animations.overlayTriggers);
});

window.addEventListener("resize", () => {
	window.ScrollTrigger?.refresh();
});
